import React, { FC } from 'react'
import { CellularPayPrompt } from './types'
import { formatDate } from './format'
import { isSuspendedStage, payableSimStage } from './sim-status'
import { isLocked } from './is-locked'

export const BACKBILL_TITLE = 'Your cellular service has expired'
export const RENEWAL_TITLE = 'Your cellular service is due for renewal'
export const SUSPENDED_TITLE = 'Your cellular service is suspended'
export const RENEWAL_PAST_DUE_TITLE = 'Your cellular renewal is past due'

// SHA-973 P3: a renewal we emailed locks the CMS once its pay-by has passed. The paywall
// decides the lock; the ledger kind it sends (`billKind`) says the bill is a renewal.
export const isRenewalBill = (prompt: CellularPayPrompt): boolean =>
  prompt.billKind === 'renewal' || prompt.billKind === 'renewal_auto'

export const isLockingRenewal = (prompt: CellularPayPrompt): boolean =>
  !prompt.isBackbill && isRenewalBill(prompt) && isLocked(prompt)

// SHA-973 P4c: the billing engine's own bills — the first year after a controller's
// included service (`first`), or a display added to the owner's subscription (`join`).
// Neither is a back-bill: nothing lapsed unpaid, Cirrus paid nothing on their behalf.
export const FIRST_BILL_TITLE = 'Your included cellular service has ended'
export const JOIN_BILL_TITLE = 'Cellular for your new display'

export const isEngineBill = (prompt: CellularPayPrompt): boolean =>
  !prompt.isBackbill && (prompt.billKind === 'first' || prompt.billKind === 'join')

// SHA-973 P4d: an old open bill the fresh start emailed (the paywall's `freshStart`,
// from the invoice's cellularFreshStart marker). Draft copy (decision 17). A notice-only
// bill (its SIM already suspended) keeps the suspended title.
export const FRESH_START_TITLE = 'We have not been able to reach you about this bill'

export const isFreshStartBill = (prompt: CellularPayPrompt): boolean =>
  !prompt.isBackbill && prompt.freshStart === true

export const freshStartDescription = (prompt: CellularPayPrompt): string => {
  const due = formatDate(prompt.dueDate)
  return (
    `We have not been able to reach you about this bill. Please pay it${due ? ` by ${due}` : ''} ` +
    `so your cellular service for ${prompt.controllers} display(s) is not interrupted.`
  )
}

export const owedTitle = (prompt: CellularPayPrompt): string => {
  if (isSuspendedStage(payableSimStage(prompt))) return SUSPENDED_TITLE
  if (isFreshStartBill(prompt)) return FRESH_START_TITLE
  if (isLockingRenewal(prompt)) return RENEWAL_PAST_DUE_TITLE
  if (isEngineBill(prompt)) return prompt.billKind === 'join' ? JOIN_BILL_TITLE : FIRST_BILL_TITLE
  return prompt.isBackbill ? BACKBILL_TITLE : RENEWAL_TITLE
}

export const engineBillDescription = (prompt: CellularPayPrompt): string => {
  const ended = formatDate(prompt.expiredOn)
  const due = formatDate(prompt.dueDate)
  const by = due ? ` by ${due}` : ''
  if (prompt.billKind === 'join') {
    return (
      `Cellular for ${prompt.controllers} display(s)${ended ? ` from ${ended}` : ''} has been ` +
      `added to your subscription. Pay the time up to your renewal below${by}.`
    )
  }
  return (
    `Your included cellular service ${ended ? `ended on ${ended}` : 'has ended'}. ` +
    `To keep ${prompt.controllers} display(s) connected, pay the first year below${by}.`
  )
}

// A renewal is an ordinary bill: nothing has lapsed and we have paid nothing on the
// customer's behalf, so none of the back-bill's wording applies to it.
export const renewalDescription = (prompt: CellularPayPrompt): string => {
  const due = formatDate(prompt.dueDate)
  const when = !due ? 'is due for renewal' : `${prompt.daysPastDue > 0 ? 'was' : 'is'} due on ${due}`
  return (
    `Your cellular service for ${prompt.controllers} display(s) ${when}. ` +
    'Pay the amount below to keep it connected.'
  )
}

export const SUPPORT_EMAIL = 'support@cirrusled.com'
export const SUPPORT_PHONE = '(877) 636-2331'

// The PM's words (2026-09-30); only the expiry date changes.
const BackbillDescription: FC<{ prompt: CellularPayPrompt }> = ({ prompt }) => (
  <>
    {prompt.expiredOn
      ? `Your cellular service expired on ${formatDate(prompt.expiredOn)}. `
      : 'Your cellular service has expired. '}
    Since then, Cirrus has continued paying for it on your behalf. To keep using the connectivity
    service, please pay the missed payments below. If you believe this to be in error please contact
    us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or call{' '}
    <a href='tel:+18776362331'>{SUPPORT_PHONE}</a>, Option 2.
  </>
)

export const OwedDescription: FC<{ prompt: CellularPayPrompt }> = ({ prompt }) => {
  if (prompt.isBackbill) return <BackbillDescription prompt={prompt} />
  if (isFreshStartBill(prompt)) return <>{freshStartDescription(prompt)}</>
  if (isEngineBill(prompt)) return <>{engineBillDescription(prompt)}</>
  return <>{renewalDescription(prompt)}</>
}
